import { supabase } from '../lib/supabase';

export const I18N_LOCALE_CODES = ['en', 'es', 'pt', 'ru'] as const;
export type I18nLocaleCode = (typeof I18N_LOCALE_CODES)[number];

export type I18nValueStatus = 'draft' | 'published' | 'needs_review';
export type I18nValueSource = 'manual' | 'ai' | 'import';

export interface I18nLocaleValue {
  value: unknown;
  status: I18nValueStatus;
  updated_at?: string;
}

export interface I18nResourceRow {
  resource_key: string;
  description: string | null;
  values_by_locale: Partial<Record<I18nLocaleCode, I18nLocaleValue>>;
  total_count: number;
}

export interface I18nCoverageRow {
  locale: string;
  total_keys: number;
  translated: number;
  missing: number;
  coverage_pct: number;
}

export interface SearchI18nResourcesParams {
  search?: string;
  missingLocale?: I18nLocaleCode | '';
  limit?: number;
  offset?: number;
}

export async function fetchI18nCoverage(): Promise<I18nCoverageRow[]> {
  const { data, error } = await supabase.rpc('admin_list_i18n_coverage');
  if (error) throw new Error(error.message);
  return (data ?? []) as I18nCoverageRow[];
}

export async function searchI18nResources(
  params: SearchI18nResourcesParams,
): Promise<{ rows: I18nResourceRow[]; total: number }> {
  const { data, error } = await supabase.rpc('admin_search_i18n_resources', {
    p_limit: params.limit ?? 50,
    p_missing_locale: params.missingLocale ?? '',
    p_offset: params.offset ?? 0,
    p_search: params.search?.trim() ?? '',
  });
  if (error) throw new Error(error.message);

  const list = (data ?? []) as Array<Omit<I18nResourceRow, 'total_count'> & { total_count?: number }>;
  const total = list[0]?.total_count ?? 0;
  const rows = list.map(({ resource_key, description, values_by_locale }) => ({
    resource_key,
    description,
    values_by_locale: (values_by_locale ?? {}) as I18nResourceRow['values_by_locale'],
    total_count: total,
  }));
  return { rows, total: Number(total) };
}

export async function upsertI18nResource(params: {
  resourceKey: string;
  locale: I18nLocaleCode;
  value: unknown;
  status?: I18nValueStatus;
  description?: string | null;
  source?: I18nValueSource;
}): Promise<void> {
  const { error } = await supabase.rpc('admin_upsert_i18n_resource', {
    p_resource_key: params.resourceKey.trim(),
    p_locale: params.locale,
    p_value: params.value,
    p_status: params.status ?? 'published',
    p_description: params.description ?? null,
    p_namespace: 'translation',
    p_source: params.source ?? 'manual',
  });
  if (error) throw new Error(error.message);
}

export async function deleteI18nResource(resourceKey: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_i18n_resource', {
    p_resource_key: resourceKey.trim(),
    p_namespace: 'translation',
  });
  if (error) throw new Error(error.message);
}

export async function bulkCopyI18nLocale(params: {
  fromLocale: I18nLocaleCode;
  toLocale: I18nLocaleCode;
  onlyMissing?: boolean;
  status?: I18nValueStatus;
}): Promise<number> {
  const { data, error } = await supabase.rpc('admin_bulk_copy_i18n_locale', {
    p_from_locale: params.fromLocale,
    p_to_locale: params.toLocale,
    p_only_missing: params.onlyMissing ?? true,
    p_status: params.status ?? 'draft',
  });
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

export async function fetchMissingI18nKeys(locale: I18nLocaleCode): Promise<string[]> {
  const { data, error } = await supabase.rpc('admin_list_i18n_missing_keys', {
    p_locale: locale,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{ resource_key: string }>).map((r) => r.resource_key);
}

export function valueToEditString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

export function parseEditString(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    return JSON.parse(trimmed) as unknown;
  }
  return raw;
}

export function previewValue(value: unknown, max = 48): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

export interface I18nExportRow {
  resource_key: string;
  description: string | null;
  en: unknown;
  es: unknown;
  pt: unknown;
  ru: unknown;
}

export interface I18nAuditEntry {
  id: string;
  resource_key: string;
  locale: string | null;
  action: string;
  old_value: unknown;
  new_value: unknown;
  meta: unknown;
  changed_by: string | null;
  created_at: string;
}

export async function exportI18nResources(): Promise<I18nExportRow[]> {
  const { data, error } = await supabase.rpc('admin_export_i18n_resources');
  if (error) throw new Error(error.message);
  return (data ?? []) as I18nExportRow[];
}

export async function fetchI18nAuditLog(limit = 100, offset = 0): Promise<I18nAuditEntry[]> {
  const { data, error } = await supabase.rpc('admin_list_i18n_audit_log', {
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as I18nAuditEntry[];
}

export async function runAiTranslateBatch(params: {
  toLocale: I18nLocaleCode;
  fromLocale?: I18nLocaleCode;
  onlyMissing?: boolean;
  batchSize?: number;
  maxBatches?: number;
}): Promise<{ translated: number; batches: number }> {
  const { data, error } = await supabase.functions.invoke('i18n-translate-batch', {
    body: {
      to_locale: params.toLocale,
      from_locale: params.fromLocale ?? 'en',
      only_missing: params.onlyMissing ?? true,
      batch_size: params.batchSize ?? 25,
      max_batches: params.maxBatches ?? 4,
    },
  });
  if (error) throw new Error(error.message);
  const payload = data as { error?: string; translated?: number; batches?: number };
  if (payload?.error) throw new Error(payload.error);
  return { translated: Number(payload.translated ?? 0), batches: Number(payload.batches ?? 0) };
}

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) return `"${cell.replace(/"/g, '""')}"`;
  return cell;
}

function jsonCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function buildI18nCsv(rows: I18nExportRow[]): string {
  const header = ['resource_key', 'description', 'en', 'es', 'pt', 'ru'];
  const lines = [header.join(',')];
  for (const row of rows) {
    lines.push(
      [
        csvEscape(row.resource_key),
        csvEscape(row.description ?? ''),
        csvEscape(jsonCell(row.en)),
        csvEscape(jsonCell(row.es)),
        csvEscape(jsonCell(row.pt)),
        csvEscape(jsonCell(row.ru)),
      ].join(','),
    );
  }
  return lines.join('\n');
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function parseCellValue(raw: string): unknown {
  const t = raw.trim();
  if (!t) return null;
  if (t.startsWith('[') || t.startsWith('{')) return JSON.parse(t) as unknown;
  return t;
}

export async function importI18nFromCsv(csvText: string): Promise<number> {
  const lines = csvText.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return 0;

  const header = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase());
  const keyIdx = header.indexOf('resource_key');
  if (keyIdx < 0) throw new Error('CSV must include resource_key column');

  const descIdx = header.indexOf('description');
  const localeCols: Array<{ locale: I18nLocaleCode; idx: number }> = [];
  for (const locale of I18N_LOCALE_CODES) {
    const idx = header.indexOf(locale);
    if (idx >= 0) localeCols.push({ locale, idx });
  }

  let imported = 0;
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]!);
    const resourceKey = cols[keyIdx]?.trim();
    if (!resourceKey) continue;
    const description = descIdx >= 0 ? cols[descIdx]?.trim() || null : null;

    for (const { locale, idx } of localeCols) {
      const raw = cols[idx] ?? '';
      const value = parseCellValue(raw);
      if (value === null) continue;
      await upsertI18nResource({
        resourceKey,
        locale,
        value,
        status: 'published',
        description,
        source: 'import',
      });
      imported++;
    }
  }
  return imported;
}
